import * as React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
	easing,
	Grid,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Skeleton,
	Slide,
	useMediaQuery,
	useTheme,
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarLinesPen, faChevronsDown, faChevronsUp } from '@fortawesome/pro-solid-svg-icons';
import { faPhoneFlip, faCircleQuestion, faEnvelopeOpenText, faLink } from '@fortawesome/free-solid-svg-icons';

import { NumberUtils } from '../../utils';
import { CerebroIcon, CerebroText, CerebroTextButton } from '..';
import { useDealQuery, useIntegrationConfigQuery, useMatchingQuery } from '../../hooks';
import { CerebroLink } from '../CerebroLink';
import { Urls, useAuth } from '../../context';
import { CerebroPhoneNumber, CerebroPhoneNumberHref, LoanRequestUrl } from '../../constants';
import { DataRoomRootUrl, PlatformRootUrl } from '../../utils/platform';

export const StatusBar: React.FC<StatusBarProps> = props => {
	const theme = useTheme();

	const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

	if (!isDesktop) return <MobileStatusBar {...props} />;

	return <DesktopStatusBar />;
};

const MobileHeaderHeight = 64;

export const MobileStatusBarListItemHeight = 46;
const NumberOfListItems = 5;

const MobileStatusBarOpenPosition = MobileHeaderHeight;
const MobileStatusBarClosedPosition = MobileHeaderHeight - MobileStatusBarListItemHeight * NumberOfListItems;
const MobileStatusBarLoadingPosition = MobileStatusBarClosedPosition - MobileStatusBarListItemHeight;

const MobileStatusBar: React.FC<StatusBarProps> = ({}) => {
	const theme = useTheme();
	const { guid } = useParams<'guid'>();
	const { isSuccess, matches, requestedAmount } = useStatusData();

	const location = useLocation();

	const [open, setOpen] = React.useState(false);

	let top = MobileStatusBarLoadingPosition;

	if (isSuccess) top = open ? MobileStatusBarOpenPosition : MobileStatusBarClosedPosition;

	React.useEffect(() => {
		setOpen(false);
	}, [location, setOpen]);

	return (
		<List
			dense
			disablePadding
			sx={{
				backgroundColor: theme.palette.gray.background,
				borderBottom: `2px solid ${theme.palette.primary.main}`,
				boxShadow: theme.yellowShadows[4],
				boxSizing: 'border-box',

				top,
				position: 'absolute',
				width: '100%',

				transition: theme.transitions.create(['all']),

				zIndex: 100,
			}}
		>
			<ListItem disableGutters>
				{/* @ts-ignore */}
				<ListItemButton
					component={Link}
					to={`${guid}/${Urls.DealTypePreference.Root}`}
					sx={{ justifyContent: 'space-between' }}
				>
					<ListItemText>
						<CerebroText component="span" weight="medium">
							Lender Matches
						</CerebroText>
					</ListItemText>
					<CerebroText component="span" weight="medium">
						{matches === 0 ? '--' : matches}
					</CerebroText>
				</ListItemButton>
			</ListItem>

			<ListItem disableGutters>
				{/* @ts-ignore */}
				<ListItemButton
					component={Link}
					to={`${guid}/${Urls.Deal.DebtCapital}`}
					sx={{ justifyContent: 'space-between' }}
				>
					<ListItemText>
						<CerebroText component="span" weight="medium">
							Amount Requested
						</CerebroText>
					</ListItemText>
					<CerebroText component="span" weight="medium">
						{requestedAmount === 0 ? '--' : NumberUtils.FormatCurrency(requestedAmount)}
					</CerebroText>
				</ListItemButton>
			</ListItem>

			<ListItem disableGutters>
				<ListItemButton component="a" href={BuildEmailUrl()}>
					<ListItemText disableTypography>
						<CerebroText align="center" component="span" weight="medium" sx={{ display: 'inline-block' }}>
							Email Us
						</CerebroText>
					</ListItemText>
				</ListItemButton>
			</ListItem>

			<ListItem disableGutters>
				<ListItemButton component="a" href={LoanRequestUrl}>
					<ListItemText disableTypography>
						<CerebroText align="center" component="span" weight="medium" sx={{ display: 'inline-block' }}>
							Schedule a Call
						</CerebroText>
					</ListItemText>
				</ListItemButton>
			</ListItem>

			<ListItem disableGutters divider>
				<ListItemButton component="a" href={CerebroPhoneNumberHref}>
					<ListItemText disableTypography>
						<CerebroText align="center" component="span" weight="medium" sx={{ display: 'inline-block' }}>
							Call {CerebroPhoneNumber}
						</CerebroText>
					</ListItemText>
				</ListItemButton>
			</ListItem>

			<ListItem disableGutters>
				<ListItemButton onClick={() => setOpen(currentValue => !currentValue)}>
					<ListItemText>
						<CerebroText component="span" weight="medium">
							{open ? 'Close' : 'Get Help'}
						</CerebroText>
					</ListItemText>
					<ListItemIcon sx={{ justifyContent: 'flex-end' }}>
						<FontAwesomeIcon icon={open ? faChevronsUp : faChevronsDown} />
					</ListItemIcon>
				</ListItemButton>
			</ListItem>
		</List>
	);
};

const DesktopStatusBar: React.FC = () => {
	const theme = useTheme();
	const { guid } = useParams<'guid'>();
	const { currentUser } = useAuth();
	const isEmployeeUser = currentUser?.isEmployeeUser ?? false;

	const { isSuccess, isFetching, matches, requestedAmount, dataRoomGuid, companyGuid, hubspotId, hubspotPortalId } =
		useStatusData();

	const [helpMenuAnchorElement, setHelpMenuAnchorElement] = React.useState<null | HTMLElement>(null);

	const helpMenuOpen = Boolean(helpMenuAnchorElement);
	const onOpenHelpMenu = (event: React.MouseEvent<HTMLButtonElement>) =>
		setHelpMenuAnchorElement(event.currentTarget);
	const onCloseHelpMenu = () => setHelpMenuAnchorElement(null);

	return (
		<Slide mountOnEnter in={isSuccess} easing={easing.easeOut} timeout={500}>
			<div>
				<Grid
					container
					flexWrap="nowrap"
					alignItems="center"
					justifyContent="space-between"
					sx={{
						borderBottom: `2px solid ${theme.palette.primary.main}`,
						boxShadow: theme.yellowShadows[4],
						paddingX: 2,
						paddingY: 1,
					}}
				>
					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						<Grid item sx={{ marginRight: 3 }}>
							<CerebroText variant="small" gutterBottom align="center" weight="medium">
								Amount Requested
							</CerebroText>
							{isFetching ? (
								<Skeleton
									animation="wave"
									sx={theme => ({ backgroundColor: theme.palette.primary.light })}
								/>
							) : (
								<CerebroLink
									component={Link}
									sx={{ display: 'block' }}
									to={`${guid}/${Urls.Deal.DebtCapital}`}
									variant="small"
									align="center"
								>
									{requestedAmount === 0 ? '--' : NumberUtils.FormatCurrency(requestedAmount)}
								</CerebroLink>
							)}
						</Grid>
						<Grid item>
							<CerebroText variant="small" gutterBottom align="center" weight="medium">
								Lender Matches
							</CerebroText>
							{isFetching ? (
								<Skeleton
									animation="wave"
									sx={theme => ({ backgroundColor: theme.palette.primary.light })}
								/>
							) : (
								<CerebroLink
									component={Link}
									sx={{ display: 'block' }}
									to={`${guid}/${Urls.DealTypePreference.Root}`}
									variant="small"
									align="center"
								>
									{matches === 0 ? '--' : matches}
								</CerebroLink>
							)}
						</Grid>
					</Grid>

					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						{isEmployeeUser ? (
							<>
								<CerebroTextButton
									id="basic-button"
									aria-controls={helpMenuOpen ? 'basic-menu' : undefined}
									aria-haspopup="true"
									aria-expanded={helpMenuOpen ? 'true' : undefined}
									onClick={onOpenHelpMenu}
								>
									Quick Links
									<CerebroIcon sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
										<FontAwesomeIcon icon={faLink} size="lg" />
									</CerebroIcon>
								</CerebroTextButton>
								<Menu
									id="basic-menu"
									anchorEl={helpMenuAnchorElement}
									open={helpMenuOpen}
									onClose={onCloseHelpMenu}
									MenuListProps={{
										'aria-labelledby': 'basic-button',
									}}
								>
									<MenuItem
										onClick={onCloseHelpMenu}
										component="a"
										href={`${DataRoomRootUrl}/${dataRoomGuid}`}
										target="_blank"
									>
										<ListItemText>Data Room</ListItemText>
									</MenuItem>
									<MenuItem
										onClick={onCloseHelpMenu}
										component="a"
										href={`${PlatformRootUrl}/Emp/CompanySummary/${companyGuid}`}
										target="_blank"
									>
										<ListItemText>Company Summary</ListItemText>
									</MenuItem>
									<MenuItem onClick={onCloseHelpMenu} component={Link} to={`${guid}/RelatedDeals`}>
										<ListItemText>Similar Deals</ListItemText>
									</MenuItem>
									<MenuItem
										onClick={onCloseHelpMenu}
										component="a"
										href={`https://app.hubspot.com/contacts/${hubspotPortalId}/deal/${hubspotId}/`}
										target="_blank"
									>
										<ListItemText>HubSpot</ListItemText>
									</MenuItem>
								</Menu>
							</>
						) : (
							<>
								<CerebroLink
									href={CerebroPhoneNumberHref}
									variant="small"
									align="center"
									sx={{ marginRight: 2 }}
								>
									<CerebroIcon sx={{ color: theme.palette.primary.main, marginRight: 1 }}>
										<FontAwesomeIcon icon={faPhoneFlip} />
									</CerebroIcon>
									{CerebroPhoneNumber}
								</CerebroLink>

								<CerebroTextButton
									id="basic-button"
									aria-controls={helpMenuOpen ? 'basic-menu' : undefined}
									aria-haspopup="true"
									aria-expanded={helpMenuOpen ? 'true' : undefined}
									onClick={onOpenHelpMenu}
								>
									Need Help
									<CerebroIcon sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
										<FontAwesomeIcon icon={faCircleQuestion} size="lg" />
									</CerebroIcon>
								</CerebroTextButton>
								<Menu
									id="basic-menu"
									anchorEl={helpMenuAnchorElement}
									open={helpMenuOpen}
									onClose={onCloseHelpMenu}
									MenuListProps={{
										'aria-labelledby': 'basic-button',
									}}
								>
									<MenuItem onClick={onCloseHelpMenu}>
										<ListItemIcon sx={{ color: theme.palette.primary.main }}>
											<FontAwesomeIcon icon={faPhoneFlip} />
										</ListItemIcon>
										<ListItemText>Call {CerebroPhoneNumber}</ListItemText>
									</MenuItem>

									<MenuItem
										onClick={onCloseHelpMenu}
										component="a"
										href={LoanRequestUrl}
										target="_blank"
									>
										<ListItemIcon sx={{ color: theme.palette.primary.main }}>
											<FontAwesomeIcon icon={faCalendarLinesPen} />
										</ListItemIcon>
										<ListItemText>Schedule a Call</ListItemText>
									</MenuItem>

									<MenuItem onClick={onCloseHelpMenu} component="a" href={BuildEmailUrl()}>
										<ListItemIcon sx={{ color: theme.palette.primary.main }}>
											<FontAwesomeIcon icon={faEnvelopeOpenText} />
										</ListItemIcon>
										<ListItemText>Email Us</ListItemText>
									</MenuItem>
								</Menu>
							</>
						)}
					</Grid>
				</Grid>
			</div>
		</Slide>
	);
};

function useStatusData() {
	const { guid } = useParams<'guid'>();

	const dealQuery = useDealQuery(guid);
	const matchingQuery = useMatchingQuery(guid);
	const integrationsQuery = useIntegrationConfigQuery();

	const matches =
		matchingQuery.data?.loanOptions
			?.filter(opt => opt.isMatched)
			.reduce((sum, option) => sum + option.matchedLenders, 0) ?? 0;
	const requestedAmount = dealQuery.data?.totalRequestedAmount ?? 0;
	const dataRoomGuid = dealQuery.data?.dataRoomGuid;
	const companyGuid = dealQuery.data?.companyGuid;
	const hubspotId = dealQuery.data?.hubSpotId;
	const hubspotPortalId = integrationsQuery.data?.hubspotPortalId;

	return {
		isSuccess: dealQuery.isSuccess && matchingQuery.isSuccess && integrationsQuery.isSuccess,
		isFetching: dealQuery.isFetching || matchingQuery.isFetching || integrationsQuery.isFetching,
		matches,
		requestedAmount,
		dataRoomGuid,
		companyGuid,
		hubspotId,
		hubspotPortalId,
	};
}

export interface StatusBarProps {}

const BuildEmailUrl = () =>
	`mailto:sales@cerebrocapital.com?subject=Help: Cerebro Loan Application&body=Hi, I have some questions and need help with my loan request here: ${encodeURI(
		window.location.href
	)}`;
